import styles from './UnlockModelDialog.module.scss';
import { Trans } from 'react-i18next';
import ErrorMessage from '@cas/components/state-management/error-message/ErrorMessage';
import { useTranslation } from 'react-i18next';
import {
  VdsButton,
  VdsDialog,
  VdsDialogActionFooter,
  VdsDialogBody,
  VdsDialogHeader,
  VdsDialogModal,
} from '@volt/volt-extended-components-react';
import { ReactElement, useCallback, useContext, useRef } from 'react';
import { ApiBaseQueryError, ModelName } from '@casops/store/api';
import UnlockDialogButton, {
  UnlockDialogButtonProps,
} from './components/button/UnlockDialogButton';
import { useNavigate } from 'react-router-dom';
import { SuccessNotificationContext } from '@dmc/ui';

interface UnlockModelDialogProps extends UnlockDialogButtonProps {
  modelName: ModelName;
  itemName: string;
  unlockModelCallback: () => Promise<void>;
  error: ApiBaseQueryError;
  isLoading: boolean;
  navigateTo?: string; // Optional path to navigate to after successful unlock.
}

export default function UnlockModelDialog({
  modelName,
  itemName,
  unlockModelCallback,
  error,
  isLoading,
  disabled = false,
  isIconButton = true,
  navigateTo,
}: UnlockModelDialogProps): ReactElement | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccessMessage } = useContext(SuccessNotificationContext);

  const ref = useRef<HTMLVdsDialogElement>(null);

  const onUnlockClick = useCallback(async () => {
    try {
      await unlockModelCallback();
      ref?.current?.close();
      showSuccessMessage(t(`Model.Unlock.Success`, { modelName }));
      if (navigateTo) {
        navigate(navigateTo);
      }
    } catch (err) {
      console.error(err);
    }
  }, [unlockModelCallback, showSuccessMessage, t, modelName, navigateTo, navigate]);

  if (!disabled) {
    return (
      <VdsDialog
        ref={ref}
        key={`unlock-model--${modelName}`}
        padding="spacing-07"
        backdrop={true}
        width="650px"
        className={styles.preventMisalign}
      >
        <UnlockDialogButton isIconButton={isIconButton} disabled={false} />

        <VdsDialogModal>
          <VdsDialogHeader slot="header" show-close-button={false}>
            {t('Model.Unlock.Title', { modelName })}
          </VdsDialogHeader>

          <VdsDialogBody slot="body" className={styles.body}>
            <p className={styles.content}>
              <Trans
                i18nKey={'Model.Unlock.Body'}
                values={{ modelName, itemName }}
                components={{ b: <b />, strong: <strong /> }}
              />
            </p>
            {typeof error !== 'undefined' && (
              <ErrorMessage message={t('Model.Unlock.Error', { modelName })} />
            )}
          </VdsDialogBody>

          <VdsDialogActionFooter slot="footer" class="vds__dialog__footer--end">
            <VdsButton
              variant="tertiary"
              close-dialog=""
              disabled={isLoading}
              style={{ width: 120 }}
              isFullWidth
            >
              {t('Cancel')}
            </VdsButton>
            <VdsButton
              variant="primary"
              onClick={onUnlockClick}
              disabled={isLoading}
              style={{ width: 120 }}
              isFullWidth
            >
              {t('Unlock')}
            </VdsButton>
          </VdsDialogActionFooter>
        </VdsDialogModal>
      </VdsDialog>
    );
  } else {
    return null;
  }
}
